/**
 * AI自動入力セクション コンポーネント
 * フリーテキスト入力エリアと「AIで自動入力」ボタンを提供する共通UI
 */
import { Sparkles } from "lucide-react";
import type { FC } from "react";
import { useState } from "react";

type AiAutoFillSectionProps = {
  /** AI解析中フラグ */
  loading: boolean;
  /** AIで自動入力ボタン押下時のコールバック */
  onParse: (text: string) => Promise<void>;
  /** テキストエリアのプレースホルダー */
  placeholder?: string;
  /** エラーメッセージ（外部から制御する場合） */
  errorMessage?: string | null;
};

/**
 * AI自動入力セクション
 * フリーテキストを入力し「AIで自動入力」ボタンでフォームを自動入力する
 * @param {object} props コンポーネントプロパティ
 * @param {boolean} props.loading AI解析中フラグ
 * @param {Function} props.onParse AIで自動入力ボタン押下時のコールバック
 * @param {string} [props.placeholder] テキストエリアのプレースホルダー
 * @param {string} [props.errorMessage] エラーメッセージ
 * @return {React.ReactElement} AI自動入力セクション
 */
export const AiAutoFillSection: FC<AiAutoFillSectionProps> = ({
  loading,
  onParse,
  placeholder = "人材情報や案件情報のテキストを貼り付けて「AIで自動入力」を押してください。",
  errorMessage: externalError,
}) => {
  const [sourceText, setSourceText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const displayError = externalError ?? error;

  const handleParse = async () => {
    if (!sourceText.trim()) return;
    setError(null);
    try {
      await onParse(sourceText);
    } catch {
      setError("AI解析に失敗しました。手動で入力してください。");
    }
  };

  return (
    <div className="card bg-base-200/50 shadow-sm mb-4">
      <div className="card-body py-3 px-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-primary" />
            フリーテキストからAI自動入力
          </h3>
          <button
            type="button"
            className="btn btn-primary btn-sm gap-1"
            onClick={handleParse}
            disabled={loading || !sourceText.trim()}
          >
            {loading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            AIで自動入力
          </button>
        </div>
        <textarea
          value={sourceText}
          onChange={e => setSourceText(e.target.value)}
          className="textarea textarea-bordered textarea-sm w-full"
          rows={4}
          placeholder={placeholder}
        />
        {displayError && (
          <div className="text-error text-xs mt-1">{displayError}</div>
        )}
        <p className="text-base-content/50 text-xs mt-1">
          テキストを貼り付けるだけで、以下のフィールドがAIにより自動入力されます
        </p>
      </div>
    </div>
  );
};
